import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge } from '../components/Common';
import { adminApi, rankingCriteriaApi, smsApi } from '../services/api';
import { useImportPatients } from '../hooks/usePatients';
import type { RankingCriterion } from '../types';

interface DragItem {
  id: number;
  index: number;
}

function RankingCriteriaManager() {
  const queryClient = useQueryClient();
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [localCriteria, setLocalCriteria] = useState<RankingCriterion[] | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: criteriaData, isLoading } = useQuery({
    queryKey: ['ranking-criteria'],
    queryFn: async () => {
      const response = await rankingCriteriaApi.getAll();
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (criteria: RankingCriterion[]) => {
      const input = {
        criteria: criteria.map((c, index) => ({
          id: c.id,
          weight: c.weight,
          enabled: c.enabled,
          display_order: index + 1,
        })),
      };
      return rankingCriteriaApi.update(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranking-criteria'] });
      setHasChanges(false);
      setLocalCriteria(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => rankingCriteriaApi.reset(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranking-criteria'] });
      setHasChanges(false);
      setLocalCriteria(null);
    },
  });

  const criteria = localCriteria || criteriaData || [];

  const handleDragStart = useCallback((e: React.DragEvent, item: DragItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.index === targetIndex) return;

    const newCriteria = [...criteria];
    const [removed] = newCriteria.splice(draggedItem.index, 1);
    newCriteria.splice(targetIndex, 0, removed);

    setLocalCriteria(newCriteria);
    setDraggedItem({ ...draggedItem, index: targetIndex });
    setHasChanges(true);
  }, [draggedItem, criteria]);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
  }, []);

  const handleWeightChange = useCallback((id: number, weight: number) => {
    const newCriteria = (localCriteria || criteriaData || []).map(c =>
      c.id === id ? { ...c, weight: Math.max(0, Math.min(100, weight)) } : c
    );
    setLocalCriteria(newCriteria);
    setHasChanges(true);
  }, [localCriteria, criteriaData]);

  const handleToggleEnabled = useCallback((id: number) => {
    const newCriteria = (localCriteria || criteriaData || []).map(c =>
      c.id === id ? { ...c, enabled: !c.enabled } : c
    );
    setLocalCriteria(newCriteria);
    setHasChanges(true);
  }, [localCriteria, criteriaData]);

  const handleSave = () => {
    if (localCriteria) {
      updateMutation.mutate(localCriteria);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all ranking criteria to defaults? This will undo any customizations.')) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return <div className="p-4 text-gray-500">Loading ranking criteria...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-500">
          Drag to reorder. Higher = higher priority.
        </p>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReset}
            disabled={resetMutation.isPending}
            className="flex-1 sm:flex-none"
          >
            Reset
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="flex-1 sm:flex-none"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {criteria.map((criterion, index) => (
          <div
            key={criterion.id}
            draggable
            onDragStart={(e) => handleDragStart(e, { id: criterion.id, index })}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`p-3 sm:p-4 bg-white border rounded-lg cursor-move transition-all ${
              draggedItem?.id === criterion.id ? 'opacity-50 border-blue-400' : 'border-gray-200 hover:border-gray-300'
            } ${!criterion.enabled ? 'opacity-60' : ''}`}
          >
            {/* Mobile layout */}
            <div className="flex items-start gap-3 sm:hidden">
              <div className="flex flex-col items-center gap-1 pt-0.5">
                <div className="flex flex-col gap-0.5 text-gray-400">
                  <div className="w-4 h-0.5 bg-current rounded"></div>
                  <div className="w-4 h-0.5 bg-current rounded"></div>
                  <div className="w-4 h-0.5 bg-current rounded"></div>
                </div>
                <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-sm font-medium text-gray-600">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900 text-sm">{criterion.display_name}</span>
                  <button
                    onClick={() => handleToggleEnabled(criterion.id)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                      criterion.enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        criterion.enabled ? 'translate-x-4' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{criterion.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={criterion.weight}
                    onChange={(e) => handleWeightChange(criterion.id, parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={criterion.weight}
                    onChange={(e) => handleWeightChange(criterion.id, parseInt(e.target.value) || 0)}
                    className="w-12 px-1 py-0.5 text-sm border border-gray-300 rounded text-center"
                  />
                </div>
              </div>
            </div>

            {/* Desktop layout */}
            <div className="hidden sm:flex items-center gap-4">
              <div className="flex flex-col gap-0.5 text-gray-400">
                <div className="w-4 h-0.5 bg-current rounded"></div>
                <div className="w-4 h-0.5 bg-current rounded"></div>
                <div className="w-4 h-0.5 bg-current rounded"></div>
              </div>
              <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-sm font-medium text-gray-600">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{criterion.display_name}</span>
                  {!criterion.enabled && (
                    <Badge variant="warning">Disabled</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500 truncate">{criterion.description}</p>
              </div>
              <div className="flex items-center gap-3 w-48">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={criterion.weight}
                  onChange={(e) => handleWeightChange(criterion.id, parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={criterion.weight}
                  onChange={(e) => handleWeightChange(criterion.id, parseInt(e.target.value) || 0)}
                  className="w-14 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                />
              </div>
              <button
                onClick={() => handleToggleEnabled(criterion.id)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  criterion.enabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    criterion.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        ))}
      </div>

      {hasChanges && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          You have unsaved changes. Tap Save to apply.
        </div>
      )}
    </div>
  );
}

function SmsSettings() {
  const [testPhone, setTestPhone] = useState('');
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: smsStatus, isLoading } = useQuery({
    queryKey: ['sms-status'],
    queryFn: async () => {
      const response = await smsApi.getStatus();
      return response.data;
    },
  });

  const testMutation = useMutation({
    mutationFn: () => smsApi.sendTest(testPhone),
    onSuccess: (response) => {
      setResult({ success: true, message: response.data.message });
    },
    onError: (error) => {
      setResult({ success: false, message: error instanceof Error ? error.message : 'Failed to send test SMS' });
    },
  });

  const broadcastMutation = useMutation({
    mutationFn: () => smsApi.broadcast(),
    onSuccess: (response) => {
      setResult({ success: true, message: response.data.message });
    },
    onError: (error) => {
      setResult({ success: false, message: error instanceof Error ? error.message : 'Failed to broadcast' });
    },
  });

  const handleBroadcast = () => {
    if (window.confirm('Send status update SMS to all physios with phone numbers?')) {
      broadcastMutation.mutate();
    }
  };

  if (isLoading) {
    return <Card title="SMS Notifications"><p className="text-gray-500">Loading...</p></Card>;
  }

  return (
    <Card title="SMS Notifications">
      <div className="space-y-4">
        {/* Status */}
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">SMS Status</h4>
            <p className="text-sm text-gray-500">{smsStatus?.message}</p>
          </div>
          <Badge variant={smsStatus?.configured ? 'success' : 'warning'}>
            {smsStatus?.configured ? 'Configured' : 'Not Configured'}
          </Badge>
        </div>

        {/* Result message */}
        {result && (
          <div
            className={`p-3 rounded-lg ${
              result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm">{result.message}</p>
              <button onClick={() => setResult(null)} className="text-current opacity-50">
                &times;
              </button>
            </div>
          </div>
        )}

        {smsStatus?.configured ? (
          <>
            <hr />

            {/* Test SMS */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Send Test SMS</h4>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="07700 123456"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => testMutation.mutate()}
                  isLoading={testMutation.isPending}
                  disabled={!testPhone}
                >
                  Send Test
                </Button>
              </div>
            </div>

            <hr />

            {/* Broadcast */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Daily Status Broadcast</h4>
              <p className="text-sm text-gray-500 mb-3">
                Send the current team status to all physios who have phone numbers. Shows who is active,
                sick, or unavailable.
              </p>
              <Button
                variant="primary"
                onClick={handleBroadcast}
                isLoading={broadcastMutation.isPending}
              >
                Send Status Broadcast Now
              </Button>
            </div>
          </>
        ) : (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            To enable SMS notifications, configure the following environment variables on the server:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>TWILIO_ACCOUNT_SID</li>
              <li>TWILIO_AUTH_TOKEN</li>
              <li>TWILIO_PHONE_NUMBER</li>
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

export function Settings() {
  const queryClient = useQueryClient();

  const { data: syncStatus, refetch: refetchSync } = useQuery({
    queryKey: ['sync-status'],
    queryFn: async () => {
      const response = await adminApi.getSyncStatus();
      return response.data;
    },
  });

  const importPatients = useImportPatients();

  const [isSeeding, setIsSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const patientFileRef = useRef<HTMLInputElement>(null);
  const appointmentFileRef = useRef<HTMLInputElement>(null);

  const handlePatientImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await importPatients.mutateAsync(file);
      setImportResult({
        success: result.success,
        message: `Imported ${result.data.inserted} patients, updated ${result.data.updated}. ${
          result.data.errors.length > 0 ? `Errors: ${result.data.errors.length}` : ''
        }`,
      });
    } catch (error) {
      setImportResult({
        success: false,
        message: error instanceof Error ? error.message : 'Import failed',
      });
    }

    // Reset file input
    if (patientFileRef.current) {
      patientFileRef.current.value = '';
    }
  };

  const handleAppointmentImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/appointments/import', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      setImportResult({
        success: result.success,
        message: `Imported ${result.data.inserted} appointments, updated ${result.data.updated}. ${
          result.data.errors.length > 0 ? `Errors: ${result.data.errors.length}` : ''
        }`,
      });
    } catch (error) {
      setImportResult({
        success: false,
        message: error instanceof Error ? error.message : 'Import failed',
      });
    }

    // Reset file input
    if (appointmentFileRef.current) {
      appointmentFileRef.current.value = '';
    }
  };

  const handleTriggerSync = async () => {
    try {
      await adminApi.triggerSync();
      await refetchSync();
    } catch (error) {
      console.error('Sync failed:', error);
    }
  };

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will replace all existing data with demo data. Continue?')) {
      return;
    }

    setIsSeeding(true);
    setSeedResult(null);

    try {
      const result = await adminApi.seedDatabase();
      setSeedResult({
        success: true,
        message: `Seeded ${result.data.physios} physios, ${result.data.patients} patients, and ${result.data.appointments} appointments.`,
      });
      // Invalidate all queries to refresh data
      queryClient.invalidateQueries();
    } catch (error) {
      setSeedResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to seed database',
      });
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Settings</h1>

      {/* Import Result */}
      {importResult && (
        <div
          className={`p-4 rounded-lg ${
            importResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          <div className="flex items-center justify-between">
            <p>{importResult.message}</p>
            <button onClick={() => setImportResult(null)} className="text-current opacity-50">
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Ranking Criteria */}
      <Card title="Matching Algorithm Ranking Criteria">
        <RankingCriteriaManager />
      </Card>

      {/* Data Import */}
      <Card title="Data Import">
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Import Patients from CSV</h4>
            <p className="text-sm text-gray-500 mb-3">
              Upload a CSV file exported from WriteUpp with patient data. Expected columns: WUID,
              First Name, Last Name, Email, Phone, Address, City, Postcode
            </p>
            <input
              ref={patientFileRef}
              type="file"
              accept=".csv"
              onChange={handlePatientImport}
              className="hidden"
            />
            <Button
              variant="secondary"
              onClick={() => patientFileRef.current?.click()}
              isLoading={importPatients.isPending}
            >
              Select Patient CSV
            </Button>
          </div>

          <hr />

          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Import Appointments from CSV
            </h4>
            <p className="text-sm text-gray-500 mb-3">
              Upload a CSV file with appointment data. Expected columns: ID, Patient WUID,
              Practitioner, Start Date, Start Time, End Date, End Time, Status, Notes
            </p>
            <input
              ref={appointmentFileRef}
              type="file"
              accept=".csv"
              onChange={handleAppointmentImport}
              className="hidden"
            />
            <Button variant="secondary" onClick={() => appointmentFileRef.current?.click()}>
              Select Appointment CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Demo Data */}
      <Card title="Demo Data">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Load Sample Data</h4>
              <p className="text-sm text-gray-500 mb-3">
                Populate the database with sample physios, patients, and appointments for testing
                and demonstration purposes. This will replace all existing data.
              </p>

              {seedResult && (
                <div
                  className={`p-3 rounded-lg mb-3 ${
                    seedResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm">{seedResult.message}</p>
                    <button onClick={() => setSeedResult(null)} className="text-current opacity-50">
                      &times;
                    </button>
                  </div>
                </div>
              )}

              <Button
                variant="primary"
                onClick={handleSeedDatabase}
                isLoading={isSeeding}
              >
                {isSeeding ? 'Seeding...' : 'Load Demo Data'}
              </Button>
            </div>

            <div className="text-sm text-gray-500">
              <p className="font-medium mb-1">Demo data includes:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>6 physios with various specialisms</li>
                <li>10 patients across Hampshire & Surrey</li>
                <li>84 scheduled appointments for the current week</li>
              </ul>
            </div>
          </div>
        </Card>

      {/* SMS Settings */}
      <SmsSettings />

      {/* Google Forms Sync */}
      <Card title="Google Forms Sync">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Sync Status</h4>
              <p className="text-sm text-gray-500">
                Last synced:{' '}
                {syncStatus?.last_sync?.synced_at
                  ? new Date(syncStatus.last_sync.synced_at).toLocaleString()
                  : 'Never'}
              </p>
            </div>
            <Badge variant={syncStatus?.last_sync?.last_error ? 'danger' : 'success'}>
              {syncStatus?.last_sync?.last_error ? 'Error' : 'OK'}
            </Badge>
          </div>

          {syncStatus?.last_sync?.last_error && (
            <div className="p-3 bg-red-50 text-red-800 rounded-lg text-sm">
              {syncStatus.last_sync.last_error}
            </div>
          )}

          <div className="text-sm text-gray-500">
            Total syncs: {syncStatus?.total_syncs || 0}
          </div>

          <Button variant="secondary" onClick={handleTriggerSync}>
            Trigger Manual Sync
          </Button>
        </div>
      </Card>

      {/* API Information */}
      <Card title="API Configuration">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-900">GraphHopper API</h4>
            <p className="text-sm text-gray-500">
              Distance calculations are handled via the GraphHopper API. The API key is configured
              server-side and is not exposed in the frontend.
            </p>
            <Badge variant="success" className="mt-2">
              Configured
            </Badge>
          </div>

          <hr />

          <div>
            <h4 className="text-sm font-medium text-gray-900">Google Forms Webhook</h4>
            <p className="text-sm text-gray-500 mb-2">
              To receive cancellation reports from Google Forms, configure a webhook to:
            </p>
            <code className="block p-2 bg-gray-100 rounded text-xs sm:text-sm overflow-x-auto">
              POST {window.location.origin}/api/webhooks/google-forms
            </code>
            <p className="text-sm text-gray-500 mt-2">
              Expected JSON payload: {`{ physio_name, patient_name, date, time, reason }`}
            </p>
          </div>
        </div>
      </Card>

      {/* About */}
      <Card title="About">
        <div className="space-y-2 text-sm text-gray-600">
          <p>
            <strong>Physio Scheduler</strong> - Foundations Physio
          </p>
          <p>
            This application helps coordinate replacement physios when cancellations occur. It
            automatically suggests alternative physios based on patient needs, carer schedules,
            and travel time.
          </p>
          <p className="text-gray-400">Version 1.0.0</p>
        </div>
      </Card>
    </div>
  );
}
